import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { User, Image, Mic, FileText, MapPin, Contact, Sticker } from 'lucide-react';
import { Conversation, ChatMessageType } from '@/services/chat.service';
import { cn } from '@caruso/ui';

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
}

const messageTypeIcons: Record<ChatMessageType, any> = {
  TEXT: null,
  IMAGE: Image,
  VIDEO: Image,
  AUDIO: Mic,
  DOCUMENT: FileText,
  LOCATION: MapPin,
  CONTACT: Contact,
  STICKER: Sticker,
  SYSTEM: null,
};

const statusColors: Record<string, string> = {
  OPEN: 'bg-yellow-500',
  IN_PROGRESS: 'bg-blue-500',
  WAITING: 'bg-orange-500',
  CLOSED: 'bg-gray-500',
};

const statusLabels: Record<string, string> = {
  OPEN: 'Aberta',
  IN_PROGRESS: 'Em Atendimento',
  WAITING: 'Aguardando',
  CLOSED: 'Fechada',
};

// Formatar data de forma mais legivel
const formatMessageTime = (dateStr?: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  if (isToday(date)) {
    return format(date, 'HH:mm');
  }
  if (isYesterday(date)) {
    return 'Ontem';
  }
  return format(date, 'dd/MM');
};

export function ConversationItem({ conversation, isSelected, onClick }: ConversationItemProps) {
  const lastMessage = conversation.lastMessage;
  const MessageIcon = lastMessage ? messageTypeIcons[lastMessage.messageType] : null;

  const getLastMessagePreview = () => {
    if (!lastMessage) return 'Nenhuma mensagem';

    const prefix = lastMessage.direction === 'OUTBOUND' ? 'Voce: ' : '';

    switch (lastMessage.messageType) {
      case 'IMAGE':
        return `${prefix}Imagem`;
      case 'VIDEO':
        return `${prefix}Video`;
      case 'AUDIO':
        return `${prefix}Audio`;
      case 'DOCUMENT':
        return `${prefix}Documento`;
      case 'LOCATION':
        return `${prefix}Localizacao`;
      case 'CONTACT':
        return `${prefix}Contato`;
      case 'STICKER':
        return `${prefix}Sticker`;
      case 'SYSTEM':
        return lastMessage.content;
      default:
        return `${prefix}${lastMessage.content}`;
    }
  };

  const timeDisplay = formatMessageTime(conversation.lastMessageAt);

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full px-3 py-2.5 flex items-center gap-3 transition-all text-left border-l-2',
        isSelected
          ? 'bg-brand-gold/10 border-l-brand-gold'
          : 'hover:bg-brand-gold/5 border-l-transparent'
      )}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className={cn(
          'w-11 h-11 rounded-full flex items-center justify-center',
          isSelected ? 'bg-brand-gold/30' : 'bg-brand-gold/15'
        )}>
          <User className={cn(
            'w-5 h-5',
            isSelected ? 'text-brand-gold' : 'text-brand-gold/70'
          )} />
        </div>
        {/* Status indicator */}
        <div
          className={cn(
            'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-brand-ink',
            statusColors[conversation.status]
          )}
          title={statusLabels[conversation.status]}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Nome e hora */}
        <div className="flex items-center justify-between gap-2">
          <span className={cn(
            '!font-normal truncate text-sm',
            isSelected ? 'text-brand-gold' : 'text-brand-ivory'
          )}>
            {conversation.client.name}
          </span>
          <span className="text-xs text-brand-ivory/40 flex-shrink-0">
            {timeDisplay}
          </span>
        </div>

        {/* Preview da mensagem e badge */}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="flex items-center gap-1 min-w-0">
            {MessageIcon && (
              <MessageIcon className="w-3.5 h-3.5 text-brand-ivory/40 flex-shrink-0" />
            )}
            <p className={cn(
              'text-xs truncate',
              conversation.unreadCount > 0 ? 'text-brand-ivory/80 font-medium' : 'text-brand-ivory/50'
            )}>
              {getLastMessagePreview()}
            </p>
          </div>

          {conversation.unreadCount > 0 && (
            <span className="flex-shrink-0 bg-brand-gold text-brand-ink text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
            </span>
          )}
        </div>

        {/* Tags */}
        {conversation.tags && conversation.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {conversation.tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="px-1.5 py-0.5 rounded text-[10px] truncate max-w-[80px] antialiased !font-normal text-brand-gold"
                style={{
                  backgroundColor: tag.color ? `${tag.color}20` : 'rgba(209, 183, 138, 0.2)',
                  ...(tag.color && { color: tag.color }),
                }}
              >
                {tag.name}
              </span>
            ))}
            {conversation.tags.length > 3 && (
              <span className="text-[10px] text-brand-ivory/40">
                +{conversation.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Atendente */}
        {conversation.assignedUser && (
          <p className="text-xs text-brand-ivory/40 mt-0.5 truncate">
            {conversation.assignedUser.name}
          </p>
        )}
      </div>
    </button>
  );
}
